/**
 * AstraVision Robust Browser Audio & Announcement Engine
 * 
 * Guarantees audio playback in any browser:
 * 1. Pre-unlocks AudioContext on user gesture.
 * 2. Attempts HTML5 Audio with full CORS and MIME handling.
 * 3. Falls back seamlessly to Web Speech API (speechSynthesis) if audio element fails or autoplay is blocked.
 * 4. Dispatches global events so all UI indicators update in real time.
 */

import { API_BASE } from '../config';

export interface PlayAudioOptions {
  audioUrl?: string | null;
  text: string;
  language?: string;
  voiceGender?: 'male' | 'female';
  volume?: number;
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (err: any) => void;
}

let activeAudioElement: HTMLAudioElement | null = null;
let audioContext: AudioContext | null = null;

// Ensure audio context is unlocked on user interaction
export const unlockAudioContext = () => {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (AudioCtx) {
      if (!audioContext) {
        audioContext = new AudioCtx();
      }
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
    }
  } catch (e) {
    console.debug('AudioContext unlock note:', e);
  }
};

export const stopAllBrowserAudio = () => {
  try {
    if (activeAudioElement) {
      activeAudioElement.pause();
      activeAudioElement.currentTime = 0;
      activeAudioElement = null;
    }
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    window.dispatchEvent(new CustomEvent('astravision_audio_stopped'));
  } catch (e) {
    console.debug('Error stopping audio:', e);
  }
};

export const playAnnouncementAudio = async (options: PlayAudioOptions): Promise<boolean> => {
  const {
    audioUrl,
    text,
    language = 'en',
    volume = 0.95,
    onStart,
    onEnd,
    onError
  } = options;

  unlockAudioContext();
  stopAllBrowserAudio();

  // Notify UI
  window.dispatchEvent(
    new CustomEvent('astravision_audio_started', {
      detail: { text, audioUrl, language }
    })
  );

  if (onStart) onStart();

  // 1. Try HTML5 Audio if audioUrl is provided
  if (audioUrl) {
    try {
      const fullUrl = audioUrl.startsWith('http') ? audioUrl : `${API_BASE}${audioUrl}`;
      const audio = new Audio();
      audio.crossOrigin = 'anonymous';
      audio.src = fullUrl;
      audio.volume = Math.min(1.0, Math.max(0.1, volume));
      activeAudioElement = audio;

      const playResult = await new Promise<boolean>((resolve) => {
        let isResolved = false;

        audio.onplaying = () => {
          if (!isResolved) {
            isResolved = true;
            resolve(true);
          }
        };

        audio.onended = () => {
          activeAudioElement = null;
          window.dispatchEvent(new CustomEvent('astravision_audio_stopped'));
          if (onEnd) onEnd();
        };

        audio.onerror = (e) => {
          console.warn('HTML5 Audio failed, falling back to Web Speech:', e);
          if (!isResolved) {
            isResolved = true;
            resolve(false);
          }
        };

        audio.play().catch((err) => {
          console.warn('Audio play() rejected, falling back to Web Speech:', err);
          if (!isResolved) {
            isResolved = true;
            resolve(false);
          }
        });

        // Timeout fallback if audio loading stalls
        setTimeout(() => {
          if (!isResolved) {
            isResolved = true;
            resolve(false);
          }
        }, 3500);
      });

      if (playResult) {
        return true;
      }
    } catch (err) {
      console.warn('HTML5 Audio playback setup error, using Web Speech:', err);
    }
  }

  // 2. Guaranteed Fallback: Web Speech API (speechSynthesis)
  return new Promise<boolean>((resolve) => {
    try {
    if (typeof window !== 'undefined') {
      const win = window as any;
      if (win.speechSynthesis) {
        win.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.volume = volume;
        utterance.rate = 1.0;
        utterance.pitch = 1.0;

        // Regional locale mapping
        const langCode = (language || 'en').toLowerCase();
        if (langCode === 'ta') utterance.lang = 'ta-IN';
        else if (langCode === 'hi') utterance.lang = 'hi-IN';
        else if (langCode === 'te') utterance.lang = 'te-IN';
        else if (langCode === 'ml') utterance.lang = 'ml-IN';
        else if (langCode === 'kn') utterance.lang = 'kn-IN';
        else if (langCode === 'bn') utterance.lang = 'bn-IN';
        else if (langCode === 'mr') utterance.lang = 'mr-IN';
        else if (langCode === 'gu') utterance.lang = 'gu-IN';
        else if (langCode === 'es') utterance.lang = 'es-ES';
        else if (langCode === 'fr') utterance.lang = 'fr-FR';
        else if (langCode === 'de') utterance.lang = 'de-DE';
        else if (langCode === 'ar') utterance.lang = 'ar-SA';
        else utterance.lang = 'en-US';

        utterance.onstart = () => {
          if (onStart) onStart();
        };

        utterance.onend = () => {
          win.dispatchEvent(new CustomEvent('astravision_audio_stopped'));
          if (onEnd) onEnd();
          resolve(true);
        };

        utterance.onerror = (err: any) => {
          console.error('SpeechSynthesis error:', err);
          win.dispatchEvent(new CustomEvent('astravision_audio_stopped'));
          if (onError) onError(err);
          if (onEnd) onEnd();
          resolve(false);
        };

        win.speechSynthesis.speak(utterance);
      } else {
        win.dispatchEvent(new CustomEvent('astravision_audio_stopped'));
        if (onEnd) onEnd();
        resolve(false);
      }
    } else {
      if (onEnd) onEnd();
      resolve(false);
    }
    } catch (e) {
      console.error('Fatal Web Speech error:', e);
      if (typeof window !== 'undefined') {
        (window as any).dispatchEvent(new CustomEvent('astravision_audio_stopped'));
      }
      if (onError) onError(e);
      if (onEnd) onEnd();
      resolve(false);
    }
  });
};

export interface PlayAudioSequenceItem {
  audioUrl?: string | null;
  text: string;
  language?: string;
  duration?: number;
}

export interface PlayAudioSequenceOptions {
  items: PlayAudioSequenceItem[];
  delayMs?: number;
  volume?: number;
  onStart?: () => void;
  onItemChange?: (index: number, item: PlayAudioSequenceItem) => void;
  onEnd?: () => void;
}

export const playAnnouncementSequence = async (options: PlayAudioSequenceOptions): Promise<boolean> => {
  const { items, delayMs = 1500, volume = 0.95, onStart, onItemChange, onEnd } = options;
  if (!items || items.length === 0) return false;

  unlockAudioContext();
  stopAllBrowserAudio();
  if (onStart) onStart();

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (onItemChange) onItemChange(i, item);

    await new Promise<void>((resolve) => {
      playAnnouncementAudio({
        audioUrl: item.audioUrl,
        text: item.text,
        language: item.language || 'en',
        volume,
        onEnd: () => resolve(),
        onError: () => resolve()
      });
    });

    if (i < items.length - 1 && delayMs > 0) {
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }

  if (onEnd) onEnd();
  return true;
};
